import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function HomePage() {
  const [activeTab, setActiveTab] = useState('photovoltaique');
  const [form, setForm] = useState({ nom: '', email: '', telephone: '', message: '', service: '' });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  // Animate elements on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.style.opacity = '1'; e.target.style.transform = 'translateY(0)'; }
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.service-card, .process-step, .testimonial-card, .aid-card').forEach((el) => {
      el.style.opacity = '0'; el.style.transform = 'translateY(30px)'; el.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      setStatus('success');
      setForm({ nom: '', email: '', telephone: '', message: '', service: '' });
    } catch {
      setStatus('error');
    }
    setLoading(false);
  };

  return (
    <>
      {/* Hero */}
      <section className="hero" data-testid="hero-section">
        <div className="hero-background">
          <img src="/assets/images/ACCEUIL.jpg" alt="Maison avec panneaux solaires" className="hero-image" />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.35))' }}></div>
        </div>
        <div className="container" style={{ width: '100%' }}>
          <div className="hero-content">
            <h1 className="hero-title">
              Faites des économies d'énergie<br />
              en nous confiant vos travaux de<br />
              <span className="text-accent">rénovation énergétique</span>
            </h1>
            <p className="hero-subtitle">
              <strong>SEF Installation</strong> vous offre un accompagnement sur mesure et vous facilite l'accès aux énergies renouvelables. Démarches administratives, demandes de subventions, solutions de financement, chantier… c'est simple : on s'occupe de tout.
            </p>
            <p className="hero-cta-text">Demandez votre étude gratuite :</p>
            <div className="hero-buttons">
              <Link to="/solutions/photovoltaique" className="hero-btn hero-btn-solar" data-testid="hero-btn-solar">
                <div className="btn-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z"/></svg>
                </div>
                <span>Passer au solaire</span>
              </Link>
              <Link to="/solutions/pompes-chaleur" className="hero-btn hero-btn-heating">
                <div className="btn-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M17 8C8 10 5.9 16.17 3.82 20.49L5.71 21l1-2.3A4.49 4.49 0 0 0 8 19c9-3 6-8 12-11-1 4-4 7-6 8 1.5.5 3.5.5 5-1.5-1 5-5 6.5-8 6.5 1 1 1 2 1 2-4-1-8-5-8-9 0-5 3.5-9 7-11z"/></svg>
                </div>
                <span>Changer mon chauffage</span>
              </Link>
              <Link to="/solutions/isolation" className="hero-btn hero-btn-isolation">
                <div className="btn-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M10 2L3 7v2h18V7L14 2h-4zm-2 9v6H5v-6H3v9h18v-9h-2v6h-3v-6h-3v6h-2v-6H8z"/></svg>
                </div>
                <span>Isoler ma maison</span>
              </Link>
              <a href="tel:0465845177" className="hero-btn hero-btn-contact">
                <div className="btn-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
                </div>
                <span>Parler avec un expert</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Key Services */}
      <section className="key-services">
        <div className="container">
          <div className="section-header">
            <h2>Votre rénovation <span className="text-accent">clé en main</span></h2>
            <div className="key-points">
              {['Conseillers basés en France', 'Experts en rénovation', 'Suivi personnalisé', 'Assistance pour obtenir vos aides'].map((p) => (
                <div className="key-point" key={p}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                  <span>{p}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="services-grid">
            {[
              { icon: '/assets/icons/support-team-icon-1.png', title: 'Assistance de nos experts', text: "Nous vous assistons pour adapter votre installation et projet de rénovation afin d'optimiser les performances et réduire les coûts sur vos factures.", link: '/tout-comprendre', cta: 'Nos offres' },
              { icon: '/assets/icons/Programming-Administrative-Tools-icon.png', title: 'Démarches administratives', text: "Nous vous assistons tout au long des démarches administratives et nous nous occupons de l'installation et de la réalisation de votre projet.", link: '/realisations', cta: 'Nos avis clients' },
              { icon: '/assets/icons/thumbs-up-icon.png', title: 'Garantie du matériel et SAV', text: "Vous bénéficiez de la garantie constructeur et de la garantie SEF Installation pièces et main d'œuvre ainsi que de notre service après-vente.", link: '#contact', cta: 'Demander un devis' },
            ].map((s) => (
              <div className="service-card" key={s.title}>
                <div className="service-icon"><img src={s.icon} alt={s.title} /></div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
                <Link to={s.link} className="service-link">{s.cta}</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="featured-products">
        <div className="container">
          <div className="section-header">
            <h2>Nos produits <span className="text-accent">vedettes</span></h2>
          </div>
          <div className="product-tabs">
            <div className="tab-buttons">
              {[
                { id: 'photovoltaique', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z"/></svg>, label: 'Autoconsommation' },
                { id: 'pompes-chaleur', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17 8C8 10 5.9 16.17 3.82 20.49L5.71 21l1-2.3A4.49 4.49 0 0 0 8 19c9-3 6-8 12-11-1 4-4 7-6 8 1.5.5 3.5.5 5-1.5-1 5-5 6.5-8 6.5 1 1 1 2 1 2-4-1-8-5-8-9 0-5 3.5-9 7-11z"/></svg>, label: 'Chauffage' },
                { id: 'isolation', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M10 2L3 7v2h18V7L14 2h-4zm-2 9v6H5v-6H3v9h18v-9h-2v6h-3v-6h-3v6h-2v-6H8z"/></svg>, label: 'Isolation' },
              ].map((t) => (
                <button key={t.id} className={`tab-btn ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}>
                  {t.icon}{t.label}
                </button>
              ))}
            </div>
            <div className="tab-content">
              {activeTab === 'photovoltaique' && (
                <div className="tab-pane active">
                  <div className="product-content">
                    <div className="product-text">
                      <h3>Panneaux photovoltaïques</h3>
                      <ul className="product-benefits">
                        <li><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z"/></svg>Transformez la luminosité du soleil en électricité. Consommez-la. Faites jusqu'à 80% d'économie sur votre facture.</li>
                        <li><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z"/></svg>Ne subissez plus les hausses des fournisseurs d'électricité et dites oui à l'indépendance énergétique.</li>
                      </ul>
                      <Link to="/solutions/photovoltaique" className="btn btn-primary">JE PASSE AUX PANNEAUX PHOTOVOLTAÏQUES</Link>
                    </div>
                    <div className="product-image"><img src="/assets/images/AdobeStock_208731473.jpeg" alt="Panneaux photovoltaïques" /></div>
                  </div>
                </div>
              )}
              {activeTab === 'pompes-chaleur' && (
                <div className="tab-pane active">
                  <div className="product-content">
                    <div className="product-text">
                      <h3>Pompes à chaleur</h3>
                      <ul className="product-benefits">
                        <li><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>Chauffage et climatisation écologique avec un rendement exceptionnel.</li>
                        <li><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>Jusqu'à 70% d'économies sur vos factures de chauffage.</li>
                      </ul>
                      <Link to="/solutions/pompes-chaleur" className="btn btn-primary">JE CHANGE MON CHAUFFAGE</Link>
                    </div>
                    <div className="product-image"><img src="/assets/images/AdobeStock_174899157.jpeg" alt="Pompe à chaleur" /></div>
                  </div>
                </div>
              )}
              {activeTab === 'isolation' && (
                <div className="tab-pane active">
                  <div className="product-content">
                    <div className="product-text">
                      <h3>Isolation thermique</h3>
                      <ul className="product-benefits">
                        <li><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>Améliorez le confort thermique et réduisez vos déperditions énergétiques.</li>
                        <li><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>Isolation intérieure et extérieure pour un confort optimal.</li>
                      </ul>
                      <Link to="/solutions/isolation" className="btn btn-primary">J'ISOLE MA MAISON</Link>
                    </div>
                    <div className="product-image"><img src="/assets/images/Fotolia_81463074_L.jpg" alt="Isolation thermique" /></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="installation-process">
        <div className="container">
          <div className="section-header">
            <h2>Vous souhaitez acquérir nos systèmes écologiques ?</h2>
            <p>Les différentes étapes pour être installé :</p>
          </div>
          <div className="process-steps">
            {[
              { n: 1, title: 'Prise de contact', text: "Remplissez le formulaire pour convenir d'un rendez-vous avec un éco-conseiller à votre domicile." },
              { n: 2, title: 'Étude du dossier', text: "Notre éco-conseiller réalise votre audit énergétique et vous aide à remplir les documents pour déposer vos demandes." },
              { n: 3, title: 'Visite technique', text: "Un technicien vient à votre domicile pour valider la faisabilité technique de votre installation." },
              { n: 4, title: 'Chantier', text: "Après validation de tous les organismes, nos installateurs se déplacent pour réaliser le chantier selon le cahier des charges." },
              { n: 5, title: 'Inspection', text: "100% de nos chantiers sont contrôlés par des organismes indépendants. Profitez, vous économisez." },
            ].map((s) => (
              <div className="process-step" key={s.n}>
                <div className="step-number">{s.n}</div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials">
        <div className="container">
          <div className="section-header">
            <h2>Nous avons gagné la confiance de <span className="text-accent">nombreux</span> clients</h2>
            <p>Laissons nos clients parler à notre place :</p>
          </div>
          <div className="testimonials-grid">
            {[
              { text: "Nous avons choisi SEF Installation pour la pose de panneaux photovoltaïques. Les artisans sont venus poser les panneaux, ils ont bien travaillé, proprement et je trouve que c'est rare de nos jours.", author: 'Amandine R.' },
              { text: "Le service top. Les ouvriers sont professionnels et ponctuels. De superbes économies d'énergie et d'argent.", author: 'Ouarezki M.' },
              { text: "Nous sommes ravis de tout le travail effectué, tout le monde a été au top et nous recommandons l'entreprise sans aucun problème.", author: 'Mickael D.' },
              { text: "Nous sommes très contents de notre nouvelle installation. Tout s'est passé rapidement et efficacement ! Je recommande !", author: 'Leslie P.' },
              { text: "Très satisfait de l'équipe d'installation, l'isolation extérieure de ma maison aura duré 15 jours mais le résultat est IMPECCABLE.", author: 'Frédéric M.' },
            ].map((t) => (
              <div className="testimonial-card" key={t.author}>
                <div className="stars">★★★★★</div>
                <div className="testimonial-content"><p>{t.text}</p></div>
                <div className="testimonial-author">— {t.author}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Financial Aids */}
      <section className="financial-aids">
        <div className="container">
          <div className="section-header">
            <h2>Tout comprendre sur la <span className="text-accent">rénovation énergétique</span></h2>
          </div>
          <div className="aids-grid">
            {[
              { title: 'Ma Prime Rénov\'', text: "Piloté par l'Anah, Ma Prime Rénov a pour but d'aider les particuliers qui souhaitent rénover énergétiquement leur logement. Les particuliers ont droit à une enveloppe de 20 000€ tous les 5 ans. Cette aide est soumise à conditions." },
              { title: "Certificats d'économie d'énergie", text: "Les certificats d'économies d'énergie est une prime versée par des entreprises privées. Quasiment tous les travaux sont éligibles. Les CEE peuvent financer jusqu'à 20 000€ de travaux. Cette aide est soumise à conditions." },
              { title: "Prime à l'investissement d'EDF", text: "Cette prime concerne uniquement l'installation de panneaux photovoltaïques en autoconsommation. Le montant dépend de la puissance installée. Cette aide est soumise à conditions." },
              { title: 'TVA à taux réduit', text: "L'État a mis en place une bonification du taux de TVA pour les travaux d'économies d'énergie. Certains ont un taux de 5,5% d'autres sont à 10%. Cette aide est soumise à conditions." },
            ].map((a) => (
              <div className="aid-card" key={a.title}>
                <h3>{a.title}</h3>
                <p>{a.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="contact" data-testid="contact-section">
        <div className="container">
          <div className="section-header">
            <h2>Demandez votre devis gratuit</h2>
            <p>Nos experts vous conseillent pour votre projet d'énergie renouvelable</p>
          </div>
          <div className="contact-content">
            <div className="contact-info">
              <h3>SEF Installation</h3>
              <div className="contact-item">
                <strong>Téléphone :</strong><br />
                <a href="tel:0465845177">04 65 84 51 77</a>
              </div>
              <div className="contact-item">
                <strong>Email :</strong><br />
                <a href="mailto:contact@sef-installation.fr">contact@sef-installation.fr</a>
              </div>
              <div className="contact-item">
                <strong>Adresse :</strong><br />
                8 Rue Marc Bloch<br />69007 LYON
              </div>
              <div className="contact-item">
                <strong>SIREN :</strong> 851 510 974
              </div>
              <div className="contact-item" style={{ marginTop: '25px' }}>
                <strong>Horaires :</strong><br />
                Lun - Ven : 9h - 18h<br />
                Sam : 9h - 12h
              </div>
            </div>

            <form className="contact-form" onSubmit={handleSubmit} data-testid="contact-form">
              {status === 'success' && (
                <div className="form-success" data-testid="form-success">
                  Merci ! Votre demande a bien été envoyée. Nous vous recontacterons rapidement.
                </div>
              )}
              {status === 'error' && (
                <div className="form-error" data-testid="form-error">
                  Une erreur est survenue. Veuillez réessayer ou nous appeler au 04 65 84 51 77.
                </div>
              )}
              <div className="form-group">
                <input type="text" name="nom" placeholder="Votre nom *" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} required data-testid="form-nom" />
              </div>
              <div className="form-group">
                <input type="email" name="email" placeholder="Votre email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="form-email" />
              </div>
              <div className="form-group">
                <input type="tel" name="telephone" placeholder="Votre téléphone" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} data-testid="form-telephone" />
              </div>
              <div className="form-group">
                <select name="service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid="form-service">
                  <option value="">Choisissez un service</option>
                  <option value="photovoltaique">Panneaux photovoltaïques</option>
                  <option value="pompe-chaleur">Pompe à chaleur</option>
                  <option value="isolation">Isolation thermique</option>
                  <option value="renovation-globale">Rénovation globale</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div className="form-group">
                <textarea name="message" placeholder="Décrivez votre projet *" rows="5" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required data-testid="form-message"></textarea>
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '15px', fontSize: '16px' }} disabled={loading} data-testid="form-submit">
                {loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
